import GoogleMaterialIcon from '@/components/icons/google-material-icon/google-material-icon';
import { Vue, Component, Prop } from 'vue-property-decorator';
import moment from 'moment';
import { groupsApi } from '@/providers/apis/groups/groups';
import { ClassProgressReportModel } from '@/models/groups/class';
import { getGradeRange, sortByProperty } from '@/utils/utils';
import { SubjectModel } from '@/models/groups/subjects';
import { formatTime } from '@/utils/date';
import DateRangePicker from 'vue2-daterange-picker';
import { classAPI } from '@/providers/apis/class/class';
import { ClassProgressTSModel } from '@/models/progress-report/progress-report';
import CpStudentDetails from './cp-student-details/cp-student-details';
import PdfGenerator from '@/components/pdf-generator/pdf-generator';

@Component({
    name: 'class-progress-report',
    components: {
        'g-icon': GoogleMaterialIcon,
        'date-range-picker': DateRangePicker,
        'cp-student-details': CpStudentDetails,
        'pdf-generator': PdfGenerator,
    },
})

export default class ClassProgressReport extends Vue {

    // ---------------------------------------------------------------
    // Properties

    private membersList: ClassProgressReportModel | any = {};

    private studentDetails: ClassProgressTSModel[] = [];

    private collectionData: any = null;

    @Prop()
    private classData!: any;

    @Prop()
    private activeSubject!: SubjectModel;

    @Prop()
    private activeYear!: string;

    @Prop()
    private activeMonth!: string;

    private timeSpendDetails: any = {};

    private overviewReport: any = {};

    private selectedDate: string = moment().format('YYYY-MM-DD');

    private dateRange: any = {
        startDate: moment().startOf('month').format('YYYY-MM-DD'),
        endDate: new Date(),
    };

    private showStudentDetails: boolean = false;

    private activeMember: ClassProgressReportModel = {} as ClassProgressReportModel;

    private isLoading: boolean = false;

    private isDetailLoading: boolean = false;

    private isShowReport: boolean = false;

    private isReportByDate: boolean = false;

    private totalMasteryCount: any = 0;

    private diagnosticGainedStatus: any = 0;

    private analyticsDataReport: any = {
        lessonStatsData: {},
        suggestionStatsData: {},
        streakStatsData: {},
        masteredStatsData: {},
    };

    private overallReportHeaders: any = {
        masteredList: 'New Competencies Mastered (score ≥ 80%)',
        diagnostic: 'Diagnostics Gains',
        reinforcedList: 'Reinforced Gains',
        growthList: 'Targets for Growth (51% - 79%)',
        concernList: 'Areas of Concern ( < 51%)',
        assessmentInProgress: 'Incomplete Competencies',
    };

    private get pdfPropData() {
        return {
            activeMember: this.activeMember,
            showStudentDetails: this.showStudentDetails,
            classData: this.classData,
            analyticsDataReport: this.analyticsDataReport,
            isReportByDate: this.isReportByDate,
            isDetailLoading: this.isDetailLoading,
            overallReportHeaders: this.overallReportHeaders,
            isShowReport: this.isShowReport,
            overviewReport: this.overviewReport,
            dateRange: this.dateRange,
            membersList: this.membersList,
            showOverflowContent: true,
        };
    }

    // --------------------------------------------------------------
    // Hooks
    private created() {
        if (this.activeYear && this.activeMonth) {
            const momentDate = moment(`${this.activeYear}-${this.activeMonth}-1`);
            const isCurrentMonth = moment().format('MM') === this.activeMonth;
            this.dateRange = {
                startDate: momentDate.startOf('month').format('YYYY-MM-DD'),
                endDate: isCurrentMonth
                    ? moment().format('YYYY-MM-DD')
                    : momentDate.endOf('month').format('YYYY-MM-DD'),
            };
        }
        this.fetchMembersList(this.dateRange);
    }

    // ----------------------------------------------------------------
    // Actions

    private onClose() {
        this.$emit('onClose');
    }

    private updateValues() {
        this.showStudentDetails = false;
        this.fetchMembersList(this.dateRange);
    }

    private onSelectStudent(member: any) {
        this.isDetailLoading = true;
        this.activeMember = member;
        const dateRange = this.dateRange;
        const params = {
            classId: this.classData.id,
            userId: member.id,
            from: moment(dateRange.startDate).format('YYYY-MM-DD'),
            to: moment(dateRange.endDate).format('YYYY-MM-DD'),
        };
        this.showStudentDetails = true;
        this.fetchData(params);
        this.fetchDashboardReport(params);
        this.fetchClassCompetencyReport(params);
    }

    private onCloseDetail() {
        this.showStudentDetails = false;
    }

    private onGeneratePdf() {
        const classpdfLoader: any = this.$refs.classpdfLoader;
        classpdfLoader.onGeneratePdf();
    }

    /**
     * @action Help to view report for the collection or assessment
     */

    // --------------------------------------------------------------
    // Methods

    private fetchMembersList(dateRange: any) {
        this.isLoading = true;
        const activeSubject = this.activeSubject;
        const params: any = {
            fromDate: moment(dateRange.startDate).format('YYYY-MM-DD'),
            toDate: moment(dateRange.endDate).format('YYYY-MM-DD'),
        };
        if (activeSubject) {
            params.subjectCode = activeSubject.code;
        }
        groupsApi.fetchClassProgressReport(this.classData.id, params)
            .then((members: ClassProgressReportModel | any) => {
                sortByProperty(members.students, 'firstName', 'ASC');
                this.membersList = members;
                this.isLoading = false;
            });
    }

    private getGradeRange(value: number) {
        return getGradeRange(value);
    }


    private formatTime(value: number) {
        return formatTime(value);
    }

    private fetchData(params: any) {
        classAPI.fetchClassTimespent(params).then((content) => {
            this.studentDetails = content;
            this.isDetailLoading = false;
        });
    }

    private fetchClassCompetencyReport(params: any) {
        classAPI.fetchClassCompetencyReport(params).then((content) => {
            this.overviewReport = content;
        });
    }

    private fetchDashboardReport(params: any) {
        const sendParms = {
            classIds: [params.classId],
            userId: params.userId,
            from: params.from,
            to: params.to,
        };
        this.fetchLessonStats(sendParms);
        this.fetchSuggestionStats(sendParms);
        this.fetchStreakStats(sendParms);
        this.fetchMasteredStats(sendParms);
    }

    private fetchLessonStats(params: any) {
        classAPI.fetchLessonStats(params).then((data: any) => {
            this.analyticsDataReport.lessonStatsData = data;
        });
    }

    private fetchSuggestionStats(params: any) {
        classAPI.fetchSuggestionStats(params).then((data: any) => {
            this.analyticsDataReport.suggestionStatsData = data;
        });
    }

    private fetchStreakStats(params: any) {
        classAPI.fetchStreakStats(params).then((data: any) => {
            this.analyticsDataReport.streakStatsData = data;
        });
    }

    private fetchMasteredStats(params: any) {
        classAPI.fetchMasteredStats(params).then((data: any) => {
            this.analyticsDataReport.masteredStatsData = data;
        });
    }

    private beforeDownload(configObj: any) {
        const {html2pdf, options, pdfContent} = configObj;
        html2pdf().set(options).from(pdfContent).toPdf().get('pdf').then((pdf: any) => {
            (this.$refs.classpdfLoader as any).isLoading = false;
        }).save();
    }
}
